import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Send, Filter, MessageSquareText, Bell, Clock, Play, Users } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FILTER = {
  species: "any",
  has_visited: false,
  min_visit_count: "",
  bought_product_id: "",
  bought_brand_id: "",
  last_groomed_days_ago: "",
};

export default function Marketing() {
  return (
    <div className="space-y-5" data-testid="marketing-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Marketing</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Customer Outreach</h1>
        <p className="text-sm text-muted-foreground mt-1">Filter pet parents, send WhatsApp campaigns and tune grooming reminders.</p>
      </div>

      <Tabs defaultValue="campaigns">
        <TabsList>
          <TabsTrigger value="campaigns" data-testid="tab-mk-campaigns"><MessageSquareText className="w-4 h-4 mr-1" />Campaigns</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="tab-mk-reminders"><Bell className="w-4 h-4 mr-1" />Grooming Reminders</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-mk-history"><Clock className="w-4 h-4 mr-1" />History</TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns" className="mt-4">
          <CampaignBuilder />
        </TabsContent>
        <TabsContent value="reminders" className="mt-4">
          <ReminderSettings />
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <CampaignHistory />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CampaignBuilder() {
  const [filter, setFilter] = useState(EMPTY_FILTER);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [message, setMessage] = useState("Hi {{customer_name}}, we miss {{pet_name}} at Furmigo! Show this message for a special treat on your next visit. 🐾");

  useEffect(() => {
    api.get("/products").then(r => setProducts(r.data)).catch(() => {});
    api.get("/brands").then(r => setBrands(r.data)).catch(() => {});
  }, []);

  const buildPayload = () => {
    const payload = {};
    if (filter.species && filter.species !== "any") payload.species = filter.species;
    if (filter.has_visited) payload.has_visited = true;
    if (filter.min_visit_count) payload.min_visit_count = parseInt(filter.min_visit_count);
    if (filter.bought_product_id) payload.bought_product_id = filter.bought_product_id;
    if (filter.bought_brand_id) payload.bought_brand_id = filter.bought_brand_id;
    if (filter.last_groomed_days_ago !== "") payload.last_groomed_days_ago = parseInt(filter.last_groomed_days_ago);
    return payload;
  };

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/marketing/customers/filter", buildPayload());
      setResults(data);
      setSelected(new Set(data.customers.map(c => c.id)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Filter failed");
    } finally { setBusy(false); }
  };

  const toggle = (id) => {
    setSelected(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const send = async () => {
    if (selected.size === 0) { toast.error("Pick at least one recipient"); return; }
    if (!message || message.trim().length < 5) { toast.error("Message too short"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/marketing/whatsapp/send-bulk", {
        customer_ids: Array.from(selected),
        message,
        kind: "marketing",
      });
      toast.success(`Campaign sent: ${data.sent} delivered, ${data.skipped} skipped, ${data.failed} failed`);
      setSendOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-[360px_1fr] gap-4">
      <Card className="p-5 space-y-3" data-testid="marketing-filter-card">
        <div className="flex items-center gap-2 font-heading font-semibold"><Filter className="w-4 h-4" />Filters</div>

        <div>
          <Label className="text-xs">Species (parent of)</Label>
          <Select value={filter.species} onValueChange={v => setFilter(f => ({ ...f, species: v }))}>
            <SelectTrigger data-testid="filter-species"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              <SelectItem value="dog">Dog parent</SelectItem>
              <SelectItem value="cat">Cat parent</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox" checked={filter.has_visited}
            onChange={e => setFilter(f => ({ ...f, has_visited: e.target.checked }))}
            data-testid="filter-has-visited"
          />
          Has visited at least once
        </label>

        <div>
          <Label className="text-xs">Min visit count</Label>
          <Input
            type="number" min="0" placeholder="e.g. 3 = constant visitor"
            value={filter.min_visit_count}
            onChange={e => setFilter(f => ({ ...f, min_visit_count: e.target.value }))}
            data-testid="filter-min-visits"
          />
        </div>

        <div>
          <Label className="text-xs">Bought product</Label>
          <Select value={filter.bought_product_id || "_"} onValueChange={v => setFilter(f => ({ ...f, bought_product_id: v === "_" ? "" : v }))}>
            <SelectTrigger data-testid="filter-product"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="_">Any product</SelectItem>
              {products.slice(0, 200).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs">Bought brand</Label>
          <Select value={filter.bought_brand_id || "_"} onValueChange={v => setFilter(f => ({ ...f, bought_brand_id: v === "_" ? "" : v }))}>
            <SelectTrigger data-testid="filter-brand"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_">Any brand</SelectItem>
              {brands.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs">Last groomed at least N days ago</Label>
          <Input
            type="number" min="0" placeholder="e.g. 30"
            value={filter.last_groomed_days_ago}
            onChange={e => setFilter(f => ({ ...f, last_groomed_days_ago: e.target.value }))}
            data-testid="filter-last-groomed"
          />
        </div>

        <Button className="w-full" onClick={run} disabled={busy} data-testid="filter-run">
          <Filter className="w-4 h-4 mr-1" />{busy ? "Filtering…" : "Run filter"}
        </Button>
        <Button variant="ghost" size="sm" className="w-full" onClick={() => { setFilter(EMPTY_FILTER); setResults(null); setSelected(new Set()); }}>
          Reset
        </Button>
      </Card>

      <Card className="p-5" data-testid="marketing-results-card">
        <div className="flex items-center justify-between mb-3">
          <div className="font-heading font-semibold flex items-center gap-2">
            <Users className="w-4 h-4" />
            {results ? `${results.count} customer${results.count === 1 ? "" : "s"} matched` : "Run a filter to see customers"}
          </div>
          <Button
            disabled={!results || selected.size === 0}
            onClick={() => setSendOpen(true)}
            data-testid="open-bulk-send"
          >
            <Send className="w-4 h-4 mr-1" />Send WhatsApp ({selected.size})
          </Button>
        </div>
        <div className="divide-y divide-border max-h-[60vh] overflow-y-auto">
          {results?.customers?.map(c => (
            <label key={c.id} className="flex items-center gap-3 py-2 cursor-pointer hover:bg-muted/40 px-1 rounded" data-testid={`mk-customer-${c.id}`}>
              <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{c.name}</div>
                <div className="text-xs text-muted-foreground font-mono">{c.mobile_masked || "—"}</div>
              </div>
              <div className="flex flex-wrap gap-1 text-[10px]">
                {(c.species || []).map(s => <Badge key={s} variant="outline">{s}</Badge>)}
              </div>
              <div className="text-right text-xs w-28">
                <div className="font-mono">₹{Number(c.total_spend || 0).toFixed(0)}</div>
                <div className="text-muted-foreground">{c.visit_count} visits</div>
              </div>
            </label>
          ))}
          {results && results.customers.length === 0 && (
            <div className="text-sm text-muted-foreground py-6 text-center">No customers matched.</div>
          )}
          {!results && (
            <div className="text-sm text-muted-foreground py-12 text-center">Use the filter panel on the left, then click "Run filter".</div>
          )}
        </div>
      </Card>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-lg" data-testid="bulk-send-dialog">
          <DialogHeader><DialogTitle>Send WhatsApp campaign · {selected.size} recipients</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs">Message</Label>
            <textarea
              className="w-full border border-border rounded-md p-3 text-sm h-32 font-mono"
              value={message}
              onChange={e => setMessage(e.target.value)}
              data-testid="bulk-message"
            />
            <div className="text-xs text-muted-foreground">
              Placeholders: <code>{"{{customer_name}}"}</code>, <code>{"{{pet_name}}"}</code>.
              Sends are rate-limited to ~150/min.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Cancel</Button>
            <Button onClick={send} disabled={busy} data-testid="confirm-bulk-send">
              <Send className="w-4 h-4 mr-1" />{busy ? "Sending…" : "Send now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReminderSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/marketing/reminders/settings").then(r => setS(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/marketing/reminders/settings", {
        grooming_enabled: s.grooming_enabled,
        grooming_interval_days: Number(s.grooming_interval_days) || 45,
        grooming_window_days: Number(s.grooming_window_days) || 3,
        grooming_message_template: s.grooming_message_template,
      });
      toast.success("Reminder settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/marketing/reminders/grooming/run-now");
      toast.success(`Sent ${data.sent} grooming reminder(s).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (!s) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <Card className="p-5 max-w-2xl space-y-4" data-testid="reminders-card">
      <label className="flex items-center gap-2 text-sm font-semibold">
        <input
          type="checkbox" checked={!!s.grooming_enabled}
          onChange={e => setS(prev => ({ ...prev, grooming_enabled: e.target.checked }))}
          data-testid="reminders-enabled"
        />
        Auto-send grooming reminders via WhatsApp
      </label>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Remind after (days)</Label>
          <Input
            type="number" min="7" max="365"
            value={s.grooming_interval_days || 45}
            onChange={e => setS(prev => ({ ...prev, grooming_interval_days: e.target.value }))}
            data-testid="reminders-interval"
          />
          <div className="text-xs text-muted-foreground mt-1">Default 45 days after last grooming visit.</div>
        </div>
        <div>
          <Label className="text-xs">Window (± days)</Label>
          <Input
            type="number" min="0" max="14"
            value={s.grooming_window_days || 3}
            onChange={e => setS(prev => ({ ...prev, grooming_window_days: e.target.value }))}
            data-testid="reminders-window"
          />
          <div className="text-xs text-muted-foreground mt-1">Buffer around the interval day.</div>
        </div>
      </div>

      <div>
        <Label className="text-xs">Message template</Label>
        <textarea
          className="w-full border border-border rounded-md p-3 text-sm h-28 font-mono"
          value={s.grooming_message_template || ""}
          onChange={e => setS(prev => ({ ...prev, grooming_message_template: e.target.value }))}
          data-testid="reminders-template"
        />
        <div className="text-xs text-muted-foreground mt-1">
          Available: <code>{"{customer_name}"}</code>, <code>{"{pet_name}"}</code>, <code>{"{days_since}"}</code>
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={save} disabled={busy} data-testid="reminders-save">Save settings</Button>
        <Button variant="outline" onClick={runNow} disabled={busy} data-testid="reminders-run-now">
          <Play className="w-4 h-4 mr-1" />Run now (preview)
        </Button>
      </div>
    </Card>
  );
}

function CampaignHistory() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/marketing/campaigns").then(r => setRows(r.data)).catch(() => {});
  }, []);
  return (
    <Card className="p-5" data-testid="campaign-history-card">
      <div className="divide-y divide-border">
        {rows.map(r => (
          <div key={r.id} className="py-3 flex items-center gap-3" data-testid={`campaign-${r.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm line-clamp-2">{r.message}</div>
              <div className="text-xs text-muted-foreground mt-1">{new Date(r.created_at).toLocaleString()} · {r.kind}</div>
            </div>
            <div className="text-right">
              <div className="text-sm"><span className="font-mono">{r.sent}</span> sent · <span className="text-muted-foreground">{r.skipped} skipped · {r.failed} failed</span></div>
              <div className="text-xs text-muted-foreground">{r.recipient_count} recipients</div>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">No campaigns yet.</div>}
      </div>
    </Card>
  );
}
